<template>
  <div class="top-container" @mouseleave="currentHover = undefined">
    <div class="header">
      <div class="header__logo">
        <Resource :src="logoUrl" class="header__logo-img" alt="logo" @click="goLink" />
        <ClientOnly :key="userInfo?.user_id || 'no-user'">
          <XLevelTag v-if="!isMobile" class="header__logo-tag" />
        </ClientOnly>
      </div>
      <ul class="header__nav pc-only">
        <li v-for="nav in filterNavs" :key="nav.name" class="header__nav-item">
          <TopDropdown
            v-if="nav.pageKey === PageKey.SCENARIOS"
            mode="grouped"
            position="bottomCenter"
            :groups="scenariosGroup"
            :nav="nav"
            trigger="hover"
            :gap="4"
          >
            <template #trigger="{ active, selected }">
              <nuxt-link
                :class="[
                  'nav-link',
                  nav.pageKey,
                  {
                    'nav-link--hover': active,
                    'nav-link--active': selected || isNavActive(nav),
                    'nav-link--no-pointer': !nav.route,
                  },
                ]"
                :to="nav.route"
                :target="nav.target"
                @click="checkItem(nav)"
                @mouseenter="currentHover = nav"
              >
                <span class="nav-link__text" :data-text="nav.name">
                  {{ nav.name }}
                </span>
                <template v-if="scenariosGroup.length">
                  <DropdownIcon
                    class="nav-link__dropdown"
                    :class="{
                      'nav-link__dropdown--active': active,
                    }"
                  />
                </template>
              </nuxt-link>
            </template>
          </TopDropdown>
          <TopDropdown
            v-else
            mode="simple"
            position="bottomCenter"
            :items="nav.children || []"
            :nav="nav"
            trigger="hover"
            :gap="4"
          >
            <template #trigger="{ active, selected }">
              <nuxt-link
                :class="[
                  'nav-link',
                  nav.pageKey,
                  {
                    'nav-link--hover': active,
                    'nav-link--active': selected || isNavActive(nav),
                    'nav-link--no-pointer': !nav.route,
                  },
                ]"
                :to="nav.route"
                :target="nav.target"
                @click="checkItem(nav)"
                @mouseenter="currentHover = nav"
              >
                <span class="nav-link__text" :data-text="nav.name">
                  {{ nav.name }}
                </span>
                <template v-if="nav?.children?.length">
                  <DropdownIcon
                    class="nav-link__dropdown"
                    :class="{
                      'nav-link__dropdown--active': active,
                    }"
                  />
                </template>
              </nuxt-link>
            </template>
          </TopDropdown>
        </li>
      </ul>

      <div class="header__extra">
        <ClientOnly :key="userInfo?.user_id || 'no-user'">
          <LoginBtn v-if="!userInfo?.user_id" />
          <template v-else>
            <HeaderUserDropdown :disabled="isMobile" :gap="[0, 6]">
              <template #trigger>
                <Resource
                  :src="userInfo?.logo_url"
                  :transform-resource="transformImage"
                  class="header__extra-user-avatar"
                  @click="handleUserClick"
                />
              </template>
            </HeaderUserDropdown>
            <XTopCreate class="header__extra-create pc-only" />
          </template>
        </ClientOnly>
        <MoreBugger
          class="header__menu m-only"
          :class="{ 'header__menu--active': mobileNavOpen }"
          @click="toggleMobileNav"
        />
      </div>
    </div>

    <MobileNavPanel
      v-model:open="mobileNavOpen"
      :navs="filterNavs"
      :scenarios-groups="scenariosGroup"
      :logo-url="logoUrl"
      @logo-click="goLink"
    />
  </div>
</template>

<script lang="ts" setup>
import { isChinese } from '@/utils/area';
import { transformImage } from '@/utils/index';
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import HeaderUserDropdown from '@rendora/common/features/user-dropdown/index.vue';
import { useBreakpoint } from '@rendora/design/index';
import { useI18n } from 'vue-i18n';
import { USER_LEVEL_KEY } from '~/api/interface';
import { SceneType } from '~/api/module/scenarios';
import DropdownIcon from '~/assets/svg/icon-dropdown.svg?component';
import defaultLogo from '~/assets/svg/logo.svg?url';
import { useScenariosTags } from '~/composables/tags';
import { USER_ADMIN } from '~/constants';
import { PageKey } from '~/constants/page';
import LoginBtn from '~/design/components/buttons/login-btn.vue';
import { useTopNav, type Nav } from '~/hooks/useNav';
import { useMobileUserPop } from '@/composables/useMobileUserPop';
import MobileNavPanel from './MobileNavPanel.vue';
import TopDropdown, { type MenuGroup } from './TopDropdown.vue';
import MoreBugger from '~/design/icons/more-bugger.svg?component';
import { collectActiveNavKeys, getNavActiveKey } from './nav-active';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const { t: $t } = useI18n();

const navs = useTopNav();
const scenariosTags = useScenariosTags();

const userInfo = useUserInfo();
const route = useRoute();
const currentRoute = ref(route?.path?.toString().split('-')[0]);
const { mobileUserPopOpener, openMobileUserPop } = useMobileUserPop();
const model = useCustomSetting();
const { hasActionPermission } = usePageAndActionPermissionData();
const permissionData = usePermissionData();
const { isDesktop } = useDevice();

const scenariosGroup: ComputedRef<MenuGroup[]> = computed(() => {
  const personal = scenariosTags.value.filter(tag => tag.scene_type === SceneType.PERSONAL);
  const enterprise = scenariosTags.value.filter(tag => tag.scene_type === SceneType.ENTERPRISE);

  const groups: MenuGroup[] = [];

  if (personal.length > 0) {
    groups.push({
      title: $t('ge-ren-jing-xuan'),
      items: personal.map(tag => ({
        name: tag.tag_name,
        route: `/scenarios/${tag.tag_outer_id}.html`,
      })),
    });
  }

  if (enterprise.length > 0) {
    groups.push({
      title: $t('qi-ye-jing-xuan'),
      items: enterprise.map(tag => ({
        name: tag.tag_name,
        route: `/scenarios/${tag.tag_outer_id}.html`,
      })),
    });
  }

  return groups;
});

// 先定义 isLogin，因为后面的 watch 会用到它
const isLogin = computed(() => route.name === 'login');

const isShowLoginPopover = ref(false);

watch(
  () => isDesktop && isChinese() && !userInfo.value?.user_id && !isLogin.value,
  val => {
    isShowLoginPopover.value = val;
  },
  { immediate: true },
);

const logoUrl = computed(() => {
  if (permissionData.value?.config?.logo_url) {
    return transformImage(permissionData.value?.config.logo_url);
  }

  return !isChinese()
    ? getFeUrl('/rendora/logo.svg')
    : (userInfo.value?.user_id && model.value?.logo.url) || defaultLogo;
});

provideResourceLocation({
  source: SOURCE.HEADER,
});

const filterNavs = computed(() => {
  let cloneNavs = [...navs.value];

  if (userInfo.value) {
    const isTeamMember =
      [
        USER_LEVEL_KEY.TEAM_LIGHT,
        USER_LEVEL_KEY.TEAM_TASTE,
        USER_LEVEL_KEY.ORG_STANDARD,
        USER_LEVEL_KEY.ORG_FLAGSHIP,
      ].includes(Number(userInfo.value?.rights_type)) &&
      userInfo.value?.permission === USER_ADMIN.NORMAL;
    if (isTeamMember && !userInfo.value?.member_can_visible_pay_view_page) {
      cloneNavs = cloneNavs.filter(item => item.pageKey !== PageKey.SUBSCRIBE);
    }
  }

  if (scenariosTags.value.length === 0) {
    cloneNavs = cloneNavs.filter(item => item.pageKey !== PageKey.SCENARIOS);
  }

  return cloneNavs;
});

const currentHover = ref<Nav | undefined>();
const mobileNavOpen = ref(false);
const activeNavKeys = computed(() => {
  return collectActiveNavKeys(filterNavs.value, route.name, route.path);
});

watch(
  () => route.path,
  next => {
    currentRoute.value = next?.toString().split('-')[0];
    mobileNavOpen.value = false;
  },
  { deep: true },
);

const handleUserClick = () => {
  if (isMobile.value && mobileUserPopOpener.value) {
    openMobileUserPop();
    return;
  }

  emits('show-user-pop');
};

const toggleMobileNav = () => {
  mobileNavOpen.value = !mobileNavOpen.value;
};

const checkItem = (item: Nav) => {
  if (item.trackEvent) {
    trackEvent(item.trackEvent);
  }
};

const isNavActive = (nav: Nav) => {
  return activeNavKeys.value.has(getNavActiveKey(nav));
};

const goLink = () => {
  if (!hasActionPermission(GENERIC_ACTION_KEYS.CLICK_LOGO_REDIRECT)) {
    return;
  }

  trackEvent(TRACK_EVENT.CLICK_LOGO);
  window.location.href = permissionData.value?.config?.logo_click_url
    ? permissionData.value.config.logo_click_url
    : window.location.origin;
};

const emits = defineEmits<{
  (e: 'show-user-pop'): void;
}>();

watch(
  () => isMobile.value,
  value => {
    if (!value) {
      mobileNavOpen.value = false;
    }
  },
);
</script>

<style lang="scss" scoped src="./styles/index.pc.scss"></style>
<style lang="scss" scoped src="./styles/index.mobile.scss"></style>
